/**
 * API keys.
 *
 * The screen is built around one moment: the secret is visible exactly once,
 * in the response that creates it, and is not recoverable afterwards by anyone
 * — including us. Everything here is arranged so nobody discovers that after
 * navigating away.
 */

import { useState, type FormEvent } from "react";

import { api, ApiError } from "../api/client";
import { useAsync } from "../hooks";
import { SkeletonRows } from "../components/Loading";
import { CopyValue } from "../components/CopyValue";
import { Icon } from "../components/Sprites";
import { KEY_PRESETS } from "../roles";
import { useSession } from "../session";
import type { ApiKey, CreatedApiKey, CursorPage, Team } from "../api/types";

export function ApiKeys() {
  const { orgId, can } = useSession();
  const [created, setCreated] = useState<CreatedApiKey | null>(null);
  const admin = can("keys:manage");

  const keys = useAsync<CursorPage<ApiKey>>(
    // Not requested at all without the role for it. Firing the call anyway
    // would put a 403 in everyone's console and teach people to ignore them.
    (signal) =>
      admin
        ? api.get<CursorPage<ApiKey>>("/v1/api-keys", { signal, query: { limit: 100 } })
        : Promise.resolve({ data: [], next_cursor: null, has_more: false }),
    [orgId, admin],
  );

  if (!admin) return <AdminOnly />;

  const rows = keys.data?.data ?? [];

  return (
    <>
      <div className="topbar">
        <h1 className="oc-headline-lg">API Keys</h1>
        <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={keys.reload}>
          <Icon name="refresh" size={12} />
          Refresh
        </button>
      </div>

      <div className="content">
        {created && <RevealedKey created={created} onDismiss={() => setCreated(null)} />}

        <CreateKey
          onCreated={(key) => {
            setCreated(key);
            keys.reload();
          }}
        />

        <div className="oc-panel oc-starfield" style={{ flex: 1, minWidth: 0 }}>
          <div className="oc-table keys-table" style={{ flex: 1, minHeight: 0 }}>
            <div className="oc-table__head" role="row">
              <span>Name</span>
              <span>Key</span>
              <span>Scope</span>
              <span>Last used</span>
              <span className="oc-table__cell--right">Action</span>
            </div>

            <div className="oc-table__body">
              {keys.error && (
                <p className="field-error" style={{ padding: "var(--oc-gutter)" }}>
                  {keys.error.message}
                </p>
              )}

              {keys.loading && rows.length === 0 && <SkeletonRows columns={5} />}

              {!keys.error && rows.length === 0 && !keys.loading && (
                <div className="oc-empty">
                  <div className="oc-empty__title">No API keys yet</div>
                  <div className="oc-empty__hint">
                    A key is how anything outside this dashboard sends mail. Create one, scope it to
                    a team where you can, and store it somewhere you can find it again.
                  </div>
                </div>
              )}

              {rows.map((key) => (
                <KeyRow key={key.id} apiKey={key} onRevoked={keys.reload} />
              ))}
            </div>

            <div className="oc-table__footer">
              <span className="oc-mono-sm">{rows.length === 0 ? "No keys" : `${rows.length} keys`}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * The secret, shown once.
 *
 * Deliberately hard to miss and deliberately hard to dismiss by accident: the
 * only recovery from closing this too early is creating another key.
 */
function RevealedKey({ created, onDismiss }: { created: CreatedApiKey; onDismiss: () => void }) {
  return (
    <div className="oc-panel oc-panel--attention" role="alert">
      <div className="oc-panel__header">
        <div>
          <div className="oc-label" style={{ marginBottom: 4 }}>
            New key — copy it now
          </div>
          <div className="oc-body-sm oc-text-secondary">
            This is the only time <strong>{created.name}</strong> will be shown. Only a hash is
            stored, so it cannot be retrieved later — a lost key is replaced, not recovered.
          </div>
        </div>
        <button type="button" className="oc-btn oc-btn--sm" onClick={onDismiss}>
          I have stored it
        </button>
      </div>
      <div className="oc-panel__body">
        <div className="oc-dns-value">
          <CopyValue value={created.key} label="Copy API key" className="oc-truncate" />
        </div>
      </div>
    </div>
  );
}

/**
 * Not a permission failure to apologise for — the whole screen is
 * administrative, and saying why is more useful than an empty table.
 */
function AdminOnly() {
  return (
    <>
      <div className="topbar">
        <h1 className="oc-headline-lg">API Keys</h1>
      </div>
      <div className="content">
        <div className="oc-panel oc-starfield" style={{ flex: 1 }}>
          <div className="oc-empty">
            <div className="oc-empty__title">Keys are managed by owners and admins</div>
            <div className="oc-empty__hint">
              A key is a credential for the whole organization or one team, so both the list and the
              controls are limited to the roles that administer it. Ask an owner for a key scoped to
              what you need.
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function KeyRow({ apiKey, onRevoked }: { apiKey: ApiKey; onRevoked: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = keyState(apiKey);

  const revoke = async () => {
    if (
      !window.confirm(
        `Revoke "${apiKey.name}"? Anything using it stops working on its next request, and the key cannot be restored.`,
      )
    ) {
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await api.delete(`/v1/api-keys/${encodeURIComponent(apiKey.id)}`);
      onRevoked();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not revoke the key.");
      setBusy(false);
    }
  };

  return (
    <div className="oc-table__row" role="row" style={{ cursor: "default" }}>
      <span style={{ minWidth: 0 }}>
        <div className="oc-body-sm oc-truncate">{apiKey.name}</div>
        <div className={`oc-mono-sm ${state.className}`}>{state.label}</div>
      </span>

      <span style={{ minWidth: 0 }}>
        {/* The prefix, never the key. Enough to match against one someone is
            holding, useless as a credential. */}
        <CopyValue value={apiKey.prefix} display={`${apiKey.prefix}…`} className="oc-truncate" />
      </span>

      <span style={{ minWidth: 0 }}>
        <div className="oc-body-sm oc-text-secondary">
          {apiKey.team_id ? "One team" : "Organization"}
        </div>
        {/* Absent capabilities mean the key predates them and carries whatever
            its scope implies — said in words, because a blank cell here would
            read as "none" and those are opposite meanings. */}
        <div className="oc-mono-sm oc-text-muted-aa oc-truncate">
          {apiKey.capabilities
            ? `${apiKey.capabilities.length} capabilit${apiKey.capabilities.length === 1 ? "y" : "ies"}`
            : "full access"}
        </div>
      </span>

      <span className="oc-mono-sm oc-text-secondary">
        {apiKey.last_used_at ? new Date(apiKey.last_used_at).toLocaleDateString() : "never"}
      </span>

      <span className="oc-table__cell--right">
        {!apiKey.revoked_at && (
          <button
            type="button"
            className="oc-btn oc-btn--danger oc-btn--sm"
            disabled={busy}
            onClick={() => void revoke()}
          >
            Revoke
          </button>
        )}
        {error && <span className="field-error">{error}</span>}
      </span>
    </div>
  );
}

/** Revoked, expired and live are three different facts, and read differently. */
function keyState(key: ApiKey): { label: string; className: string } {
  if (key.revoked_at) {
    return {
      label: `revoked ${new Date(key.revoked_at).toLocaleDateString()}`,
      className: "oc-text-danger",
    };
  }
  if (key.expires_at && new Date(key.expires_at) < new Date()) {
    return {
      label: `expired ${new Date(key.expires_at).toLocaleDateString()}`,
      className: "oc-text-warning",
    };
  }
  if (key.expires_at) {
    return {
      label: `expires ${new Date(key.expires_at).toLocaleDateString()}`,
      className: "oc-text-muted",
    };
  }
  return { label: `created ${new Date(key.created_at).toLocaleDateString()}`, className: "oc-text-muted" };
}

function CreateKey({ onCreated }: { onCreated: (key: CreatedApiKey) => void }) {
  const { orgId } = useSession();
  const [name, setName] = useState("");
  const [teamId, setTeamId] = useState("");
  // Defaults to the narrowest useful set rather than to full access. The
  // default is what most keys end up carrying, so it should be the one that is
  // safe to leak.
  const [presetId, setPresetId] = useState("read-only");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const teams = useAsync<CursorPage<Team>>(
    (signal) => api.get<CursorPage<Team>>("/v1/teams", { signal, query: { limit: 100 } }),
    [orgId],
  );

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;

    setBusy(true);
    setError(null);
    try {
      const preset = KEY_PRESETS.find((p) => p.id === presetId);
      const created = await api.post<CreatedApiKey>("/v1/api-keys", {
        name: name.trim(),
        team_id: teamId || undefined,
        // Omitted entirely for "full access", which is what NULL means on the
        // column. Sending an empty array would ask for a key that can do
        // nothing at all — the opposite of the intent, and refused.
        capabilities: preset?.capabilities,
      });
      setName("");
      setTeamId("");
      setPresetId("read-only");
      onCreated(created);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create the key.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="oc-panel" onSubmit={(event) => void submit(event)}>
      <div className="oc-panel__body row" style={{ gap: "var(--oc-space-2)", alignItems: "flex-end" }}>
        <div className="grow login__field">
          <label className="oc-label" htmlFor="key-name">
            Name
          </label>
          <input
            id="key-name"
            className="oc-input"
            placeholder="billing-service"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        <div className="login__field" style={{ width: 220 }}>
          <label className="oc-label" htmlFor="key-team">
            Scope
          </label>
          <select
            id="key-team"
            className="oc-input"
            value={teamId}
            onChange={(event) => setTeamId(event.target.value)}
          >
            {/* Organization-wide is offered but not the encouraged default:
                it sees every team, which is rarely what one integration
                needs and makes a leak as wide as the organization. */}
            <option value="">Whole organization</option>
            {(teams.data?.data ?? []).map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
        </div>

        {/* Scope and access answer different questions and sit side by side.
            Scope is *whose* resources the key can reach; access is what it may
            do with them. A key can be organization-wide and read-only, or
            team-scoped and able to send. */}
        <div className="login__field" style={{ width: 220 }}>
          <label className="oc-label" htmlFor="key-access">
            Access
          </label>
          <select
            id="key-access"
            className="oc-input"
            value={presetId}
            onChange={(event) => setPresetId(event.target.value)}
          >
            {KEY_PRESETS.map((preset) => (
              <option key={preset.id} value={preset.id}>
                {preset.label}
              </option>
            ))}
          </select>
        </div>

        <button type="submit" className="oc-btn oc-btn--primary" disabled={busy || !name.trim()}>
          <Icon name="plus" size={12} />
          {busy ? "Creating…" : "Create key"}
        </button>
      </div>

      {/* What the chosen preset actually means, always visible rather than
          behind a hover. The person minting a credential is deciding what it
          can do if it leaks, and a label alone does not tell them. */}
      <div className="oc-panel__body" style={{ paddingTop: 0 }}>
        <span className="oc-body-sm oc-text-secondary">
          {KEY_PRESETS.find((p) => p.id === presetId)?.summary}
        </span>
      </div>

      {error && (
        <div className="oc-panel__body" style={{ paddingTop: 0 }}>
          <p className="field-error">
            {error}
          </p>
        </div>
      )}
    </form>
  );
}
